import {
  type ScoreboardResponseRelatedScheduleResponseRelatedPagedData,
  type SportFilter,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { Filter } from '@on3/ui-lib/components/Filter/Filter';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { webUrl } from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { getHighSchoolSportLabel } from '@on3/ui-lib/utils/url';
import { BreadCrumb } from '@public/components/BreadCrumb/BreadCrumb';
import { HighSchoolNav } from '@public/components/HighSchool/Nav/HighSchoolNav';
import { On3Avatar } from '@public/components/Image/On3Avatar';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { MasseyRatings } from '@public/components/UI/MasseyRatings/MasseyRatings';
import { AdminLink } from '@public/components/User/AdminLink';
import { ScheduleSchema } from '@public/store/schemas/Schedule';
import {
  createCanonicalUrl,
  createDescription,
  createTitle,
} from '@public/utils/meta';
import { usePathname } from 'next/navigation';

import { HighSchoolSchedule } from './Schedule/HighSchoolSchedule';
import styles from './SchedulePage.module.scss';

export interface IHighSchoolScheduleProps extends IGenericPageProps {
  schedule?: ScoreboardResponseRelatedScheduleResponseRelatedPagedData;
  filters: {
    sports?: SportFilter[];
    years?: number[];
  };
}

export const SchedulePage = (props: IHighSchoolScheduleProps) => {
  const { schedule, siteData, filters } = props;
  const { relatedModel, list, pagination } = schedule || {};
  const { year, sport, organization } = relatedModel || {};
  const sportSlug = slugify(sport?.name || 'football');
  const sportLabel = sport ? getHighSchoolSportLabel(sport) : '';

  const { sports, years } = filters;

  const { defaultYears } = siteData || {};

  const isCurrentYear = year === defaultYears?.footballSeasonYear;
  const highSchoolName = organization?.name || 'High School';
  const highSchoolUrl = `/high-school/${organization?.urlSlug}/`;
  const primaryColor = adjustColorForReadability(
    organization?.primaryColor ?? '#fe3b1f',
  );

  const title = createTitle(
    highSchoolName,
    isCurrentYear ? '' : year,
    sportLabel,
    'Schedule',
  );
  const description = createDescription(
    `View the {0} {1} {2} Schedule at on3.com. The {3} {2} schedule includes opponents, date, time, and results.`,
    year,
    highSchoolName,
    sportLabel,
    organization?.mascot,
  );

  const noIndex = (pagination?.count ?? 0) === 0;

  const sluggedSport = slugify(sport?.name || 'football');

  const pathname = usePathname();
  const canonical = createCanonicalUrl(pathname);

  const schema = new ScheduleSchema({
    name: highSchoolName,
    url: webUrl + highSchoolUrl,
    sport: sport?.name,
    event: list,
  }).toJSON();

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      noIndex={noIndex}
      schema={schema}
      title={title}
    >
      <AdSidebarLayout>
        <AdminLink
          path={`/organization/${organization?.urlSlug}/schedule/`}
          query={{
            year: year?.toString(),
            sport: sport?.name?.toLowerCase(),
          }}
          title="Edit Schedule"
        />
        <BreadCrumb
          color={primaryColor}
          crumbs={[
            { title: 'High School', href: '/high-school/' },
            { title: highSchoolName, href: highSchoolUrl },
          ]}
          title={title}
        />

        <div className={styles.block}>
          <header className={styles.header}>
            <div className={styles.title}>
              <figure>
                <On3Avatar
                  altText={title}
                  domain={organization?.defaultAsset?.domain}
                  source={organization?.defaultAsset?.source}
                  teamAvatar
                />
              </figure>
              <h1>{title}</h1>
            </div>
            <MasseyRatings type="prediction" />
            <nav style={{ color: primaryColor }}>
              {sports && (
                <Filter
                  name="sport"
                  navigate={(_, filterSport) =>
                    `${highSchoolUrl}${filterSport}/${year}/schedule/`
                  }
                  options={sports?.map((s) => ({
                    label: getHighSchoolSportLabel(s),
                    value: slugify(s.name),
                  }))}
                  value={sluggedSport}
                />
              )}
              {years && (
                <Filter
                  name="year"
                  navigate={(_, filterYear) =>
                    `${highSchoolUrl}${sportSlug}/${filterYear}/schedule/`
                  }
                  options={years.map((y) => ({
                    label: String(y),
                    value: String(y),
                  }))}
                  value={String(year)}
                />
              )}
              <HighSchoolNav />
            </nav>
          </header>
          <HighSchoolSchedule
            full
            relatedModel={relatedModel}
            schedule={list}
          />
        </div>
      </AdSidebarLayout>
    </PageWrapper>
  );
};
